/**
 * 🔄 Unified Data Service
 * 
 * Zentrale Abstraktionsschicht für alle Datenbankzugriffe.
 * Stellt sicher, dass die richtige Datenbank für den richtigen Zweck verwendet wird.
 * 
 * REGEL: Nur dieser Service greift direkt auf RTDB/Firestore/Auth/Storage zu!
 * 
 * RE-EXPORTIERT bestehende Firestore-Funktionen und ergänzt fehlende RTDB-Funktionen.
 */

import { ref, get, set, update, remove, onValue, off, onChildAdded, onChildChanged, onChildRemoved } from 'firebase/database';
import { collection, addDoc, getDocs, doc, getDoc, updateDoc, deleteDoc, setDoc, query, orderBy, where, increment, serverTimestamp } from 'firebase/firestore';
import { 
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider
} from 'firebase/auth';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { database, firestore, auth, storage } from './config';

// Re-export existing Firestore functions (already well-structured)
export { 
  // User Profile
  getUserProfile,
  createUserProfile,
  updateUserProfile,
  getAllUsers,
  
  // Courses  
  getCourses,
  getAllCourses,
  getCourse,
  createCourse,
  updateCourse,
  deleteCourse,
  
  // Course Invites
  getCourseInvite,
  createCourseInvite,
  updateCourseInvite,
  deleteCourseInvite,
  getCourseInvites,
  getInviteByCode,
  createInvite,
  updateInvite,
  deleteInvite,
  
  // Templates
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  
  // Instances
  getInstances,
  getAllInstances,
  getInstance,
  createInstance,
  updateInstance,
  deleteInstance,
  
  // Student Stats
  getStudentStats,
  createStudentStats,
  updateStudentStats,
  setStudentStats,
  addQuizHistory,
  getQuizHistory,
  addRecentActivity,
  getCourseStats,
  updateCourseStats,
  
  // Allowed Domains
  getAllowedDomains,
  
  // Batch operations
  batchWrite
} from './firestore';

// ============================================
// 📌 CONFIGURATION: What goes where?
// ============================================

export const DATA_SOURCES = {
  // Static data → Firestore
  userProfile: 'firestore',      // users/{uid}
  courseInvites: 'firestore',    // course_invites/{code}
  quizTemplates: 'firestore',    // quiz_templates/{id}
  studentStats: 'firestore',     // student_stats/{uid}
  courses: 'firestore',          // courses/{id} (migrated)
  
  // Live data → Realtime DB
  liveSessions: 'rtdb',          // quiz_instances/{userId}/{id} (live state)
  sessionCodes: 'rtdb',          // session_codes/{code}
  courseEnrollmentsView: 'rtdb', // course_enrollments/{teacherId}/{courseId}
  
  // Hybrid: Static + Live
  quizInstances: 'hybrid'        // Firestore for meta, RTDB for live state
};

// ============================================
// 🎮 LIVE SESSION SERVICE (RTDB - sub-100ms latency)
// ============================================

export const LiveSessionService = {
  /**
   * Get session info by code (quick lookup)
   */
  async getByCode(code) {
    const codeRef = ref(database, `session_codes/${code}`);
    const snapshot = await get(codeRef);
    return snapshot.exists() ? snapshot.val() : null;
  },
  
  /**
   * Subscribe to full session state
   */
  subscribe(userId, instanceId, callback) {
    const sessionRef = ref(database, `quiz_instances/${userId}/${instanceId}`);
    const unsubscribe = onValue(sessionRef, (snapshot) => {
      callback(snapshot.exists() ? snapshot.val() : null);
    });
    return unsubscribe;
  },
  
  /**
   * Subscribe to participants (incremental updates)
   */
  subscribeParticipants(userId, instanceId, callbacks) {
    const participantsRef = ref(database, `quiz_instances/${userId}/${instanceId}/participants`);
    
    // Initial load
    get(participantsRef).then((snapshot) => {
      if (callbacks.onInitial) {
        callbacks.onInitial(snapshot.exists() ? snapshot.val() : {});
      }
    });
    
    // Incremental updates
    const handleAdded = onChildAdded(participantsRef, (snapshot) => {
      if (callbacks.onAdded) callbacks.onAdded(snapshot.key, snapshot.val());
    });
    
    const handleChanged = onChildChanged(participantsRef, (snapshot) => {
      if (callbacks.onChanged) callbacks.onChanged(snapshot.key, snapshot.val());
    });
    
    const handleRemoved = onChildRemoved(participantsRef, (snapshot) => {
      if (callbacks.onRemoved) callbacks.onRemoved(snapshot.key);
    });
    
    return () => {
      off(participantsRef, 'child_added', handleAdded);
      off(participantsRef, 'child_changed', handleChanged);
      off(participantsRef, 'child_removed', handleRemoved);
    };
  },
  
  /**
   * Subscribe to answers for a specific question
   */
  subscribeAnswers(userId, instanceId, questionIndex, callback) {
    const answersRef = ref(database, `quiz_instances/${userId}/${instanceId}/answers/${questionIndex}`);
    const unsubscribe = onValue(answersRef, (snapshot) => {
      callback(snapshot.exists() ? snapshot.val() : {});
    });
    return unsubscribe;
  },
  
  /**
   * Subscribe to specific session fields
   */
  subscribeField(userId, instanceId, field, callback) {
    const fieldRef = ref(database, `quiz_instances/${userId}/${instanceId}/${field}`);
    const unsubscribe = onValue(fieldRef, (snapshot) => {
      callback(snapshot.exists() ? snapshot.val() : null);
    });
    return unsubscribe;
  },
  
  /**
   * Join session as participant
   */
  async joinAsParticipant(userId, instanceId, participantId, participantData) {
    const participantRef = ref(database, `quiz_instances/${userId}/${instanceId}/participants/${participantId}`);
    await set(participantRef, {
      ...participantData,
      joinedAt: Date.now(),
      score: 0
    });
  },
  
  /**
   * Leave session
   */
  async leaveSession(userId, instanceId, participantId) {
    const participantRef = ref(database, `quiz_instances/${userId}/${instanceId}/participants/${participantId}`);
    await remove(participantRef);
  },
  
  /**
   * Submit answer
   */
  async submitAnswer(userId, instanceId, questionIndex, participantId, answerData) {
    const answerRef = ref(database, `quiz_instances/${userId}/${instanceId}/answers/${questionIndex}/${participantId}`);
    await set(answerRef, {
      ...answerData,
      answeredAt: Date.now()
    });
  },
  
  /**
   * Update participant score
   */
  async updateParticipantScore(userId, instanceId, participantId, score) {
    const scoreRef = ref(database, `quiz_instances/${userId}/${instanceId}/participants/${participantId}/score`);
    await set(scoreRef, score);
  },
  
  // ============================================
  // TEACHER CONTROLS
  // ============================================
  
  /**
   * Set current question
   */
  async setQuestion(userId, instanceId, questionIndex) {
    await update(ref(database, `quiz_instances/${userId}/${instanceId}`), {
      currentQuestion: questionIndex,
      showResults: false,
      showCorrectAnswer: false,
      answersLocked: false
    });
  },
  
  /**
   * Toggle show results
   */
  async toggleResults(userId, instanceId, show) {
    await update(ref(database, `quiz_instances/${userId}/${instanceId}`), {
      showResults: show
    });
  },
  
  /**
   * Toggle correct answer visibility
   */
  async toggleCorrectAnswer(userId, instanceId, show) {
    await update(ref(database, `quiz_instances/${userId}/${instanceId}`), {
      showCorrectAnswer: show
    });
  },
  
  /**
   * Lock/unlock answers
   */
  async lockAnswers(userId, instanceId, locked) {
    await update(ref(database, `quiz_instances/${userId}/${instanceId}`), {
      answersLocked: locked
    });
  },
  
  /**
   * Update timer state
   */
  async updateTimer(userId, instanceId, timerState) {
    await update(ref(database, `quiz_instances/${userId}/${instanceId}`), {
      timerState
    });
  },
  
  /**
   * Create session code mapping
   */
  async createSessionCode(code, userId, instanceId) {
    const codeRef = ref(database, `session_codes/${code}`);
    await set(codeRef, {
      userId,
      instanceId,
      createdAt: Date.now()
    });
  },
  
  /**
   * Delete session code
   */
  async deleteSessionCode(code) {
    const codeRef = ref(database, `session_codes/${code}`);
    await remove(codeRef);
  },
  
  /**
   * Initialize live session state in RTDB
   */
  async initializeLiveState(userId, instanceId, initialState = {}) {
    const sessionRef = ref(database, `quiz_instances/${userId}/${instanceId}`);
    await set(sessionRef, {
      currentQuestion: -1,  // Lobby
      showResults: false,
      showCorrectAnswer: false,
      answersLocked: false,
      timerState: null,
      participants: {},
      answers: {},
      ...initialState
    });
  },
  
  /**
   * Get all answers (for leaderboard/statistics)
   */
  async getAllAnswers(userId, instanceId) {
    const answersRef = ref(database, `quiz_instances/${userId}/${instanceId}/answers`);
    const snapshot = await get(answersRef);
    return snapshot.exists() ? snapshot.val() : {};
  },
  
  /**
   * Get all participants
   */
  async getAllParticipants(userId, instanceId) {
    const participantsRef = ref(database, `quiz_instances/${userId}/${instanceId}/participants`);
    const snapshot = await get(participantsRef);
    return snapshot.exists() ? snapshot.val() : {};
  }
};

// ============================================
// 📚 COURSE ENROLLMENT VIEW (RTDB for teacher live view)
// ============================================

export const CourseEnrollmentService = {
  /**
   * Get all enrollments for a course (teacher view)
   */
  async getEnrollments(teacherId, courseId) {
    const enrollmentsRef = ref(database, `course_enrollments/${teacherId}/${courseId}`);
    const snapshot = await get(enrollmentsRef);
    if (!snapshot.exists()) return [];
    
    return Object.entries(snapshot.val()).map(([studentId, data]) => ({
      studentId,
      ...data
    }));
  },
  
  /**
   * Subscribe to enrollments (live updates for teacher)
   */
  subscribeEnrollments(teacherId, courseId, callback) {
    const enrollmentsRef = ref(database, `course_enrollments/${teacherId}/${courseId}`);
    const unsubscribe = onValue(enrollmentsRef, (snapshot) => {
      if (!snapshot.exists()) {
        callback([]);
        return;
      }
      const enrollments = Object.entries(snapshot.val()).map(([studentId, data]) => ({
        studentId,
        ...data
      }));
      callback(enrollments);
    });
    return unsubscribe;
  },
  
  /**
   * Add enrollment (called when student joins course)
   */
  async addEnrollment(teacherId, courseId, studentId, data) {
    const enrollmentRef = ref(database, `course_enrollments/${teacherId}/${courseId}/${studentId}`);
    await set(enrollmentRef, {
      ...data,
      enrolledAt: Date.now(),
      status: 'active'
    });
  },
  
  /**
   * Remove enrollment
   */
  async removeEnrollment(teacherId, courseId, studentId) {
    const enrollmentRef = ref(database, `course_enrollments/${teacherId}/${courseId}/${studentId}`);
    await remove(enrollmentRef);
  }
};

// ============================================
// 🔧 LEGACY RTDB PATHS (for backward compatibility)
// ============================================

export const LegacyRTDBService = {
  /**
   * Get sessions from legacy path
   */
  async getSessions() {
    const sessionsRef = ref(database, 'sessions');
    const snapshot = await get(sessionsRef);
    if (!snapshot.exists()) return [];
    
    return Object.entries(snapshot.val()).map(([id, data]) => ({
      id,
      ...data
    }));
  },
  
  /**
   * Get courses from RTDB (legacy)
   */
  async getCourses(userId) {
    const coursesRef = ref(database, `courses/${userId}`);
    const snapshot = await get(coursesRef);
    if (!snapshot.exists()) return [];
    
    return Object.entries(snapshot.val()).map(([id, data]) => ({
      id,
      ...data
    }));
  }
};

// ============================================
// � GENERIC REALTIME DB SERVICE (Abstraction Layer)
// ============================================

/**
 * RealtimeDBService - Generic RTDB operations
 * 
 * Use this for custom paths or when LiveSessionService doesn't cover your use case.
 * This layer abstracts the RTDB implementation for easier migration.
 */
export const RealtimeDBService = {
  /**
   * Get a ref object for a path (for direct RTDB operations if needed)
   */
  getRef(path) {
    return ref(database, path);
  },
  
  /**
   * Read data once from a path
   */
  async get(path) {
    const dataRef = ref(database, path);
    const snapshot = await get(dataRef);
    return snapshot.exists() ? snapshot.val() : null;
  },
  
  /**
   * Write data to a path (overwrites)
   */
  async set(path, data) {
    const dataRef = ref(database, path);
    await set(dataRef, data);
  },
  
  /**
   * Update data at a path (merges)
   */
  async update(path, data) {
    const dataRef = ref(database, path);
    await update(dataRef, data);
  },
  
  /**
   * Remove data at a path
   */
  async remove(path) {
    const dataRef = ref(database, path);
    await remove(dataRef);
  },
  
  /**
   * Subscribe to value changes at a path
   * Returns unsubscribe function
   */
  subscribe(path, callback) {
    const dataRef = ref(database, path);
    const unsubscribe = onValue(dataRef, (snapshot) => {
      callback(snapshot.exists() ? snapshot.val() : null);
    });
    return unsubscribe;
  },
  
  /**
   * Subscribe to child events at a path
   * Returns unsubscribe function
   */
  subscribeChildren(path, callbacks) {
    const dataRef = ref(database, path);
    const handlers = [];
    
    if (callbacks.onAdded) {
      handlers.push(['child_added', onChildAdded(dataRef, (snapshot) => {
        callbacks.onAdded(snapshot.key, snapshot.val());
      })]);
    }
    
    if (callbacks.onChanged) {
      handlers.push(['child_changed', onChildChanged(dataRef, (snapshot) => {
        callbacks.onChanged(snapshot.key, snapshot.val());
      })]);
    }
    
    if (callbacks.onRemoved) {
      handlers.push(['child_removed', onChildRemoved(dataRef, (snapshot) => {
        callbacks.onRemoved(snapshot.key);
      })]);
    }
    
    return () => {
      handlers.forEach(([eventType]) => {
        off(dataRef, eventType);
      });
    };
  },
  
  /**
   * Get server time offset (for timer synchronization)
   * Returns a promise with the offset in ms
   */
  subscribeServerTimeOffset(callback) {
    const offsetRef = ref(database, '.info/serverTimeOffset');
    return onValue(offsetRef, (snapshot) => {
      callback(snapshot.val() || 0);
    });
  }
};

// ============================================
// �🔧 UTILITY FUNCTIONS
// ============================================

/**
 * Generate a 6-character session code (no ambiguous chars)
 */
export function generateSessionCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Generate a 6-character invite code
 */
export function generateInviteCode() {
  return generateSessionCode(); // Same format
}

// ============================================
// 🐛 BUG REPORT SERVICE (Firestore)
// ============================================

export const BugReportService = {
  /**
   * Submit a new bug report
   */
  async submit(bugReport) {
    const bugReportsRef = collection(firestore, 'bugReports');
    const reportData = {
      ...bugReport,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(bugReportsRef, reportData);
    return docRef.id;
  },
  
  /**
   * Get all bug reports (ordered by date)
   */
  async getAll() {
    const bugReportsRef = collection(firestore, 'bugReports');
    const q = query(bugReportsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  },
  
  /**
   * Update bug report status or priority
   */
  async update(reportId, updates) {
    const reportRef = doc(firestore, 'bugReports', reportId);
    await updateDoc(reportRef, {
      ...updates,
      updatedAt: new Date()
    });
  },
  
  /**
   * Delete a bug report
   */
  async delete(reportId) {
    await deleteDoc(doc(firestore, 'bugReports', reportId));
  }
};

// ============================================
// 🌐 SYSTEM CONFIG SERVICE (Firestore)
// ============================================

export const SystemConfigService = {
  /**
   * Get allowed domains configuration
   */
  async getAllowedDomains() {
    const configRef = doc(firestore, 'system_config', 'allowed_domains');
    const configSnap = await getDoc(configRef);
    return configSnap.exists() ? configSnap.data() : null;
  }
};

// ============================================
// 📊 QUESTION STATS SERVICE (Firestore)
// ============================================

export const QuestionStatsService = {
  /**
   * Get question stats for a student
   */
  async get(studentId, questionId) {
    const statsRef = doc(firestore, `student_stats/${studentId}/questionStats/${questionId}`);
    const statsSnap = await getDoc(statsRef);
    return statsSnap.exists() ? statsSnap.data() : null;
  },
  
  /**
   * Get all question stats for a student
   */
  async getAll(studentId) {
    const statsRef = collection(firestore, `student_stats/${studentId}/questionStats`);
    const snapshot = await getDocs(statsRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  },
  
  /**
   * Get stats for a specific course
   */
  async getByCourse(studentId, courseId) {
    const statsRef = collection(firestore, `student_stats/${studentId}/questionStats`);
    const q = query(statsRef, where('courseId', '==', courseId));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  },
  
  /**
   * Create or update question stats
   */
  async upsert(studentId, questionId, data) {
    const statsRef = doc(firestore, `student_stats/${studentId}/questionStats/${questionId}`);
    await setDoc(statsRef, data, { merge: true });
  },
  
  /**
   * Update existing stats with increment
   */
  async updateWithIncrement(studentId, questionId, updates) {
    const statsRef = doc(firestore, `student_stats/${studentId}/questionStats/${questionId}`);
    await updateDoc(statsRef, updates);
  },
  
  // Re-export increment for use in services
  increment
};

// ============================================
// 🎓 STUDENT DATA SERVICE (RTDB)
// ============================================

/**
 * StudentDataService - Student-specific data operations
 * Used by: StudentDashboard, StudentQuizHistory, PracticeQuizView
 */
export const StudentDataService = {
  /**
   * Get quiz history for a student
   */
  async getQuizHistory(studentId) {
    const path = `users/${studentId}/quiz-history`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Get practice quizzes for a student
   */
  async getPracticeQuizzes(studentId) {
    const path = `users/${studentId}/practice-quizzes`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Get dashboard data for a student
   */
  async getDashboardData(studentId) {
    const path = `users/${studentId}/dashboard`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Get user data
   */
  async getUserData(userId) {
    const path = `users/${userId}`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Update user data
   */
  async updateUserData(userId, data) {
    const path = `users/${userId}`;
    await RealtimeDBService.update(path, data);
  },
  
  /**
   * Get courses a student is enrolled in (from RTDB)
   */
  async getEnrolledCourses(studentId) {
    const path = `users/${studentId}/enrolled_courses`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Subscribe to user data changes
   */
  subscribeUserData(userId, callback) {
    const path = `users/${userId}`;
    return RealtimeDBService.subscribe(path, callback);
  },
  
  /**
   * Get quiz results for a student in a specific quiz instance
   */
  async getQuizResult(teacherId, instanceId, studentId) {
    const path = `quiz_instances/${teacherId}/${instanceId}/participants/${studentId}`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Get all quiz results for a student (across all teachers)
   * Note: This requires knowing the teacherIds the student has participated with
   */
  async getQuizResults(teacherId, studentId) {
    // Get all quiz instances for this teacher
    const instancesPath = `quiz_instances/${teacherId}`;
    const instances = await RealtimeDBService.get(instancesPath);
    
    if (!instances) return [];
    
    const results = [];
    for (const [instanceId, instanceData] of Object.entries(instances)) {
      if (instanceData.participants && instanceData.participants[studentId]) {
        results.push({
          instanceId,
          ...instanceData.participants[studentId],
          quizTitle: instanceData.title || instanceData.quizTitle
        });
      }
    }
    
    return results;
  }
};

// ============================================
// 📋 QUIZ MANAGEMENT SERVICE (RTDB)
// ============================================

/**
 * QuizManagementService - Quiz CRUD operations
 * Used by: quizView, questionSetView, adminView
 */
export const QuizManagementService = {
  /**
   * Get a single quiz
   */
  async getQuiz(userId, quizId) {
    const path = `courses/${userId}/quizzes/${quizId}`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Get all quizzes for a user
   */
  async getQuizzes(userId) {
    const path = `courses/${userId}/quizzes`;
    const data = await RealtimeDBService.get(path);
    if (!data) return [];
    
    return Object.entries(data).map(([id, quiz]) => ({
      id,
      ...quiz
    }));
  },
  
  /**
   * Create a new quiz
   */
  async createQuiz(userId, quizId, quizData) {
    const path = `courses/${userId}/quizzes/${quizId}`;
    await RealtimeDBService.set(path, {
      ...quizData,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
  },
  
  /**
   * Update a quiz
   */
  async updateQuiz(userId, quizId, data) {
    const path = `courses/${userId}/quizzes/${quizId}`;
    await RealtimeDBService.update(path, {
      ...data,
      updatedAt: Date.now()
    });
  },
  
  /**
   * Delete a quiz
   */
  async deleteQuiz(userId, quizId) {
    const path = `courses/${userId}/quizzes/${quizId}`;
    await RealtimeDBService.remove(path);
  },
  
  /**
   * Subscribe to quizzes (live updates)
   */
  subscribeQuizzes(userId, callback) {
    const path = `courses/${userId}/quizzes`;
    return RealtimeDBService.subscribe(path, (data) => {
      if (!data) {
        callback([]);
        return;
      }
      const quizzes = Object.entries(data).map(([id, quiz]) => ({
        id,
        ...quiz
      }));
      callback(quizzes);
    });
  },
  
  /**
   * Subscribe to a single quiz
   */
  subscribeQuiz(userId, quizId, callback) {
    const path = `courses/${userId}/quizzes/${quizId}`;
    return RealtimeDBService.subscribe(path, callback);
  },
  
  /**
   * Get questions for a quiz
   */
  async getQuestions(userId, quizId) {
    const path = `courses/${userId}/quizzes/${quizId}/questions`;
    const data = await RealtimeDBService.get(path);
    if (!data) return [];
    
    // Convert to array if needed
    if (Array.isArray(data)) return data;
    return Object.values(data);
  },
  
  /**
   * Update questions for a quiz
   */
  async updateQuestions(userId, quizId, questions) {
    const path = `courses/${userId}/quizzes/${quizId}/questions`;
    await RealtimeDBService.set(path, questions);
  },
  
  /**
   * Add a question to a quiz
   */
  async addQuestion(userId, quizId, question) {
    const questions = await this.getQuestions(userId, quizId);
    questions.push(question);
    await this.updateQuestions(userId, quizId, questions);
    return questions.length - 1; // Return index
  },
  
  /**
   * Update a single question
   */
  async updateQuestion(userId, quizId, questionIndex, questionData) {
    const path = `courses/${userId}/quizzes/${quizId}/questions/${questionIndex}`;
    await RealtimeDBService.update(path, questionData);
  },
  
  /**
   * Delete a question
   */
  async deleteQuestion(userId, quizId, questionIndex) {
    const questions = await this.getQuestions(userId, quizId);
    questions.splice(questionIndex, 1);
    await this.updateQuestions(userId, quizId, questions);
  }
};

// ============================================
// 📊 RESULTS SERVICE (RTDB)
// ============================================

/**
 * ResultsService - Quiz results operations
 * Used by: resultsView, adminView
 */
export const ResultsService = {
  /**
   * Get all results for a quiz instance
   */
  async getResults(userId, instanceId) {
    const path = `quiz_instances/${userId}/${instanceId}/participants`;
    const data = await RealtimeDBService.get(path);
    if (!data) return [];
    
    return Object.entries(data).map(([id, participant]) => ({
      id,
      ...participant
    }));
  },
  
  /**
   * Get answers for a quiz instance
   */
  async getAnswers(userId, instanceId) {
    const path = `quiz_instances/${userId}/${instanceId}/answers`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Get result for a specific participant
   */
  async getParticipantResult(userId, instanceId, participantId) {
    const path = `quiz_instances/${userId}/${instanceId}/participants/${participantId}`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Subscribe to results (live updates)
   */
  subscribeResults(userId, instanceId, callback) {
    const path = `quiz_instances/${userId}/${instanceId}/participants`;
    return RealtimeDBService.subscribe(path, (data) => {
      if (!data) {
        callback([]);
        return;
      }
      const results = Object.entries(data).map(([id, participant]) => ({
        id,
        ...participant
      }));
      callback(results);
    });
  },
  
  /**
   * Get full quiz instance data (for export)
   */
  async getFullInstance(userId, instanceId) {
    const path = `quiz_instances/${userId}/${instanceId}`;
    return await RealtimeDBService.get(path);
  }
};

// ============================================
// 👥 PARTICIPANT MANAGEMENT SERVICE (RTDB)
// ============================================

/**
 * ParticipantManagementService - Manage quiz participants
 * Used by: ParticipantsTab, adminView
 */
export const ParticipantManagementService = {
  /**
   * Get all participants for a quiz instance
   */
  async getParticipants(userId, instanceId) {
    const path = `quiz_instances/${userId}/${instanceId}/participants`;
    const data = await RealtimeDBService.get(path);
    if (!data) return [];
    
    return Object.entries(data).map(([id, participant]) => ({
      id,
      odId: id,
      ...participant
    }));
  },
  
  /**
   * Remove a participant
   */
  async removeParticipant(userId, instanceId, participantId) {
    const path = `quiz_instances/${userId}/${instanceId}/participants/${participantId}`;
    await RealtimeDBService.remove(path);
  },
  
  /**
   * Update participant data
   */
  async updateParticipant(userId, instanceId, participantId, data) {
    const path = `quiz_instances/${userId}/${instanceId}/participants/${participantId}`;
    await RealtimeDBService.update(path, data);
  },
  
  /**
   * Subscribe to participants
   */
  subscribeParticipants(userId, instanceId, callback) {
    const path = `quiz_instances/${userId}/${instanceId}/participants`;
    return RealtimeDBService.subscribe(path, (data) => {
      if (!data) {
        callback([]);
        return;
      }
      const participants = Object.entries(data).map(([id, participant]) => ({
        id,
        odId: id,
        ...participant
      }));
      callback(participants);
    });
  },
  
  /**
   * Subscribe to individual participant changes
   */
  subscribeParticipantChanges(userId, instanceId, callbacks) {
    const path = `quiz_instances/${userId}/${instanceId}/participants`;
    return RealtimeDBService.subscribeChildren(path, callbacks);
  },
  
  /**
   * Clear all participants
   */
  async clearParticipants(userId, instanceId) {
    const path = `quiz_instances/${userId}/${instanceId}/participants`;
    await RealtimeDBService.remove(path);
  }
};

// ============================================
// 📈 ACTIVITY SERVICE (RTDB)
// ============================================

/**
 * ActivityService - Activity feed operations
 * Used by: ActivityFeed component
 */
export const ActivityService = {
  /**
   * Subscribe to user activities
   */
  subscribeActivities(userId, callback, limit = 20) {
    const path = `users/${userId}/activities`;
    return RealtimeDBService.subscribe(path, (data) => {
      if (!data) {
        callback([]);
        return;
      }
      
      const activities = Object.entries(data)
        .map(([id, activity]) => ({ id, ...activity }))
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
        .slice(0, limit);
      
      callback(activities);
    });
  },
  
  /**
   * Log a new activity
   */
  async logActivity(userId, activityData) {
    const path = `users/${userId}/activities/${Date.now()}`;
    await RealtimeDBService.set(path, {
      ...activityData,
      timestamp: Date.now()
    });
  },
  
  /**
   * Get recent activities
   */
  async getRecentActivities(userId, limit = 10) {
    const path = `users/${userId}/activities`;
    const data = await RealtimeDBService.get(path);
    if (!data) return [];
    
    return Object.entries(data)
      .map(([id, activity]) => ({ id, ...activity }))
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
      .slice(0, limit);
  },
  
  /**
   * Clear all activities for a user
   */
  async clearActivities(userId) {
    const path = `users/${userId}/activities`;
    await RealtimeDBService.remove(path);
  }
};

// ============================================
// 🤖 LLM TEMPLATE SERVICE (RTDB)
// ============================================

/**
 * LLMTemplateService - LLM prompt templates
 * Used by: llmQuestionGeneratorView, LLMGeneratorView2
 */
export const LLMTemplateService = {
  /**
   * Get all templates for a user
   */
  async getTemplates(userId) {
    const path = `llm-templates/${userId}`;
    const data = await RealtimeDBService.get(path);
    if (!data) return [];
    
    return Object.entries(data).map(([id, template]) => ({
      id,
      ...template
    }));
  },
  
  /**
   * Get a single template
   */
  async getTemplate(userId, templateId) {
    const path = `llm-templates/${userId}/${templateId}`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Save a template (create or update)
   */
  async saveTemplate(userId, templateId, templateData) {
    const path = `llm-templates/${userId}/${templateId}`;
    await RealtimeDBService.set(path, {
      ...templateData,
      updatedAt: Date.now()
    });
  },
  
  /**
   * Delete a template
   */
  async deleteTemplate(userId, templateId) {
    const path = `llm-templates/${userId}/${templateId}`;
    await RealtimeDBService.remove(path);
  },
  
  /**
   * Subscribe to templates
   */
  subscribeTemplates(userId, callback) {
    const path = `llm-templates/${userId}`;
    return RealtimeDBService.subscribe(path, (data) => {
      if (!data) {
        callback([]);
        return;
      }
      const templates = Object.entries(data).map(([id, template]) => ({
        id,
        ...template
      }));
      callback(templates);
    });
  }
};

// ============================================
// 🃏 JOKER SERVICE (Extension for LiveSessionService)
// ============================================

// Extend LiveSessionService with Joker functionality
LiveSessionService.registerParticipant = async function(userId, instanceId, registrationData) {
  const path = `quiz_instances/${userId}/${instanceId}/participants/${registrationData.odId}`;
  await RealtimeDBService.set(path, {
    ...registrationData,
    registeredAt: Date.now(),
    score: 0,
    jokers: registrationData.jokers || {
      fifty: { used: false },
      skip: { used: false },
      hint: { used: false }
    }
  });
};

LiveSessionService.updateJokerStatus = async function(userId, instanceId, odId, jokerId, status) {
  const path = `quiz_instances/${userId}/${instanceId}/participants/${odId}/jokers/${jokerId}`;
  await RealtimeDBService.update(path, { used: status, usedAt: status ? Date.now() : null });
};

LiveSessionService.subscribeJokers = function(userId, instanceId, odId, callback) {
  const path = `quiz_instances/${userId}/${instanceId}/participants/${odId}/jokers`;
  return RealtimeDBService.subscribe(path, callback);
};

LiveSessionService.getJokers = async function(userId, instanceId, odId) {
  const path = `quiz_instances/${userId}/${instanceId}/participants/${odId}/jokers`;
  return await RealtimeDBService.get(path);
};

// ============================================
// 🏆 LEADERBOARD SERVICE (Extension for CourseEnrollmentService)
// ============================================

// Extend CourseEnrollmentService with Leaderboard functionality
CourseEnrollmentService.getLeaderboard = async function(teacherId, courseId) {
  const path = `course_enrollments/${teacherId}/${courseId}`;
  const enrollments = await RealtimeDBService.get(path);
  
  if (!enrollments) return [];
  
  return Object.entries(enrollments)
    .map(([studentId, data]) => ({
      studentId,
      ...data,
      score: data.totalScore || 0,
      quizzesCompleted: data.quizzesCompleted || 0
    }))
    .sort((a, b) => b.score - a.score);
};

CourseEnrollmentService.updateLeaderboardScore = async function(teacherId, courseId, studentId, scoreData) {
  const path = `course_enrollments/${teacherId}/${courseId}/${studentId}`;
  await RealtimeDBService.update(path, {
    totalScore: scoreData.totalScore,
    quizzesCompleted: scoreData.quizzesCompleted,
    lastQuizAt: Date.now()
  });
};

// ============================================
// 📦 QUESTION SET SERVICE (RTDB)
// ============================================

/**
 * QuestionSetService - Question set operations
 * Used by: questionSetView
 */
export const QuestionSetService = {
  /**
   * Get all question sets for a user
   */
  async getQuestionSets(userId) {
    const path = `courses/${userId}/question_sets`;
    const data = await RealtimeDBService.get(path);
    if (!data) return [];
    
    return Object.entries(data).map(([id, set]) => ({
      id,
      ...set
    }));
  },
  
  /**
   * Get a single question set
   */
  async getQuestionSet(userId, setId) {
    const path = `courses/${userId}/question_sets/${setId}`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Create a question set
   */
  async createQuestionSet(userId, setId, setData) {
    const path = `courses/${userId}/question_sets/${setId}`;
    await RealtimeDBService.set(path, {
      ...setData,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
  },
  
  /**
   * Update a question set
   */
  async updateQuestionSet(userId, setId, data) {
    const path = `courses/${userId}/question_sets/${setId}`;
    await RealtimeDBService.update(path, {
      ...data,
      updatedAt: Date.now()
    });
  },
  
  /**
   * Delete a question set
   */
  async deleteQuestionSet(userId, setId) {
    const path = `courses/${userId}/question_sets/${setId}`;
    await RealtimeDBService.remove(path);
  },
  
  /**
   * Subscribe to question sets
   */
  subscribeQuestionSets(userId, callback) {
    const path = `courses/${userId}/question_sets`;
    return RealtimeDBService.subscribe(path, (data) => {
      if (!data) {
        callback([]);
        return;
      }
      const sets = Object.entries(data).map(([id, set]) => ({
        id,
        ...set
      }));
      callback(sets);
    });
  }
};

// ============================================
// 🗄️ DATABASE MANAGER SERVICE (RTDB)
// ============================================

/**
 * DatabaseManagerService - Database management operations
 * Used by: databaseManagerView
 */
export const DatabaseManagerService = {
  /**
   * Get all data for a user (courses, quizzes, etc.)
   */
  async getUserData(userId) {
    const path = `courses/${userId}`;
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Get all data at a path
   */
  async getData(path) {
    return await RealtimeDBService.get(path);
  },
  
  /**
   * Update data at a path
   */
  async updateData(path, data) {
    await RealtimeDBService.update(path, data);
  },
  
  /**
   * Delete data at a path
   */
  async deleteData(path) {
    await RealtimeDBService.remove(path);
  },
  
  /**
   * Export all data for backup
   */
  async exportData(userId) {
    const courses = await RealtimeDBService.get(`courses/${userId}`);
    const instances = await RealtimeDBService.get(`quiz_instances/${userId}`);
    const enrollments = await RealtimeDBService.get(`course_enrollments/${userId}`);
    
    return {
      courses,
      quiz_instances: instances,
      course_enrollments: enrollments,
      exportedAt: Date.now()
    };
  }
};

// ============================================================================
// 🔐 AUTH SERVICE - Abstraktion für Firebase Authentication
// ============================================================================
export const AuthService = {
  /**
   * Listen for auth state changes
   * @param {Function} callback - Called with user object or null
   * @returns {Function} Unsubscribe function
   */
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },

  /**
   * Get current authenticated user
   * @returns {Object|null} Current user or null
   */
  getCurrentUser() {
    return auth.currentUser;
  },

  /**
   * Get current user's ID
   * @returns {string|null} User ID or null
   */
  getCurrentUserId() {
    return auth.currentUser?.uid || null;
  },

  /**
   * Send magic link to email
   * @param {string} email - User's email
   * @param {Object} actionCodeSettings - Settings for magic link
   * @returns {Promise<void>}
   */
  async sendMagicLink(email, actionCodeSettings) {
    return sendSignInLinkToEmail(auth, email, actionCodeSettings);
  },

  /**
   * Check if current URL is a sign-in link
   * @param {string} url - URL to check (defaults to current URL)
   * @returns {boolean}
   */
  isSignInLink(url = window.location.href) {
    return isSignInWithEmailLink(auth, url);
  },

  /**
   * Complete sign-in with email link
   * @param {string} email - User's email
   * @param {string} url - Sign-in link URL (defaults to current URL)
   * @returns {Promise<Object>} UserCredential
   */
  async signInWithEmailLink(email, url = window.location.href) {
    return signInWithEmailLink(auth, email, url);
  },

  /**
   * Sign in with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<Object>} UserCredential
   */
  async signInWithEmailPassword(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  },

  /**
   * Sign out current user
   * @returns {Promise<void>}
   */
  async signOut() {
    return signOut(auth);
  },

  /**
   * Update password for current user
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  async updatePassword(newPassword) {
    const user = auth.currentUser;
    if (!user) throw new Error('Kein Benutzer angemeldet');
    return updatePassword(user, newPassword);
  },

  /**
   * Reauthenticate user with password
   * @param {string} password - Current password
   * @returns {Promise<Object>} UserCredential
   */
  async reauthenticate(password) {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error('Kein Benutzer angemeldet');
    const credential = EmailAuthProvider.credential(user.email, password);
    return reauthenticateWithCredential(user, credential);
  },

  /**
   * Change password (reauthenticate + update)
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  async changePassword(currentPassword, newPassword) {
    await this.reauthenticate(currentPassword);
    return this.updatePassword(newPassword);
  },

  /**
   * Create email credential for reauthentication
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Object} AuthCredential
   */
  createCredential(email, password) {
    return EmailAuthProvider.credential(email, password);
  }
};

// ============================================================================
// 📁 STORAGE SERVICE - Abstraktion für Firebase Storage
// ============================================================================
export const StorageService = {
  /**
   * Upload a file to Firebase Storage
   * @param {string} path - Storage path (e.g., 'quiz-images/quiz123/image.png')
   * @param {Blob|File} file - File to upload
   * @param {Object} metadata - Optional metadata
   * @returns {Promise<{url: string, path: string}>}
   */
  async upload(path, file, metadata = {}) {
    if (!storage) {
      throw new Error('Firebase Storage nicht konfiguriert');
    }
    
    const fileRef = storageRef(storage, path);
    await uploadBytes(fileRef, file, metadata);
    const url = await getDownloadURL(fileRef);
    
    return { url, path };
  },

  /**
   * Get download URL for a file
   * @param {string} path - Storage path
   * @returns {Promise<string>} Download URL
   */
  async getDownloadUrl(path) {
    if (!storage) {
      throw new Error('Firebase Storage nicht konfiguriert');
    }
    
    const fileRef = storageRef(storage, path);
    return getDownloadURL(fileRef);
  },

  /**
   * Delete a file from Storage
   * @param {string} path - Storage path
   * @returns {Promise<void>}
   */
  async delete(path) {
    if (!storage) {
      throw new Error('Firebase Storage nicht konfiguriert');
    }
    
    const fileRef = storageRef(storage, path);
    return deleteObject(fileRef);
  },

  /**
   * List all files in a folder
   * @param {string} path - Folder path
   * @returns {Promise<{items: Array, prefixes: Array}>}
   */
  async listFiles(path) {
    if (!storage) {
      throw new Error('Firebase Storage nicht konfiguriert');
    }
    
    const folderRef = storageRef(storage, path);
    const result = await listAll(folderRef);
    
    return {
      items: result.items.map(item => ({
        name: item.name,
        fullPath: item.fullPath
      })),
      prefixes: result.prefixes.map(prefix => ({
        name: prefix.name,
        fullPath: prefix.fullPath
      }))
    };
  },

  /**
   * Check if storage is available
   * @returns {boolean}
   */
  isAvailable() {
    return !!storage;
  }
};
